import api from './api';

// Legacy > Universal API — creating the API surface: a tree of routes and the operations under them.
//
// Ported from the integration-builder sandbox, creation surface only. Superadmin-only, so it lives on
// the /admin API rather than the main one.
//
// `parent_id` and `route_id` keep their snake_case wire names deliberately. They are the column names in
// uapi_routes / uapi_operations and the same names the sandbox uses, and this schema will be compared
// against that one repeatedly while the migration runs — renaming a field in transit is how a key stops
// lining up between layers.
const legacyUapiService = {
    listRoutes: () => api.get('/admin/legacy-uapi/routes'),
    createRoute: (data) => api.post('/admin/legacy-uapi/routes', data),
    updateRoute: (id, data) => api.patch(`/admin/legacy-uapi/routes/${id}`, data),
    // Deletes the branch: children and operations go with it (both FKs cascade).
    deleteRoute: (id) => api.delete(`/admin/legacy-uapi/routes/${id}`),

    // Hotels holding a Universal API credential, narrowed to the caller's context. Params are omitted
    // when null so the server applies the widest scope it is allowed rather than matching on 'null'.
    // perCredential: one row per credential rather than per hotel — for a picker (a hotel can hold one per pipeline).
    listCredentials: ({ hotelId, accountId, partnerId, perCredential } = {}) => {
        const p = new URLSearchParams();
        if (perCredential) p.append('perCredential', '1');
        if (hotelId)   p.append('hotelId', hotelId);
        if (accountId) p.append('accountId', accountId);
        if (partnerId) p.append('partnerId', partnerId);
        const qs = p.toString();
        return api.get(`/admin/legacy-uapi/credentials${qs ? `?${qs}` : ''}`);
    },

    // The BACKEND systems a hotel is bound to (uapi_hotel_access, V419) — which PMS the Universal API
    // calls for this property, with which credential, and under which x-hotelid.
    // Every Universal API request this property has made, newest first, folded to one row per request.
    // `credentialId` narrows it to one of the property's credentials; omitted, it is all of them.
    // Traces are kept 3 days, so this is a recent-activity view and not an audit trail.
    listTraces: ({ hotelId, credentialId, accessId, limit, q } = {}) => {
        const qs = new URLSearchParams({ hotelId: String(hotelId) });
        if (credentialId) qs.set('credentialId', String(credentialId));
        if (accessId) qs.set('accessId', String(accessId));
        if (limit) qs.set('limit', String(limit));
        // Free text. Matched server-side across the call, the step messages, the pruned/rejected field
        // names AND the request/response bodies — the bodies are mapped index:false so the cluster
        // cannot search them, but the lambda holds them and can.
        if (q) qs.set('q', String(q));
        return api.get(`/admin/legacy-uapi/traces?${qs}`);
    },
    // One integration record's steps (Integration activity on the Logs tab), fetched when it is opened.
    getIntegrationLogGroup: (hotelId, groupId) =>
        api.get(`/admin/legacy-uapi/traces?hotelId=${encodeURIComponent(hotelId)}&integrationGroup=${encodeURIComponent(groupId)}`),

    listAccess: (hotelId) => api.get(`/admin/legacy-uapi/access?hotelId=${hotelId}`),
    // The x-hotelid is minted by the server, not sent: it is the routing key, so a browser-supplied
    // value could collide with another property's — or be chosen to reach one.
    createAccess: (data) => api.post('/admin/legacy-uapi/access', data),
    getAccess: (id) => api.get(`/admin/legacy-uapi/access/${id}`),
    // A binding's own module list — what it allows while override_modules is 'Y'. The choices come from
    // the credential's grants, so the override narrows an entitlement and never widens one.
    addAccessModules: (id, uapiRouteIds) =>
        api.post(`/admin/legacy-uapi/access/${id}/modules`, { uapiRouteIds }),
    removeAccessModule: (id, moduleId) =>
        api.delete(`/admin/legacy-uapi/access/${id}/modules/${moduleId}`),
    // A PATCH, and only the keys that changed: the server writes exactly what it is sent, so resending a
    // whole object would blank the property code on any save that happened not to include it.
    updateAccess: (id, data) => api.patch(`/admin/legacy-uapi/access/${id}`, data),
    // Retires the binding AND its x-hotelid — anything still sending that identifier stops being routed.
    deleteAccess: (id) => api.delete(`/admin/legacy-uapi/access/${id}`),

    // Custom code (V528) — per-client before/after hooks around a backend system's integration. A snippet
    // is written for one system (marketplaceId) and attached to any of that system's bindings.
    // { marketplaceId, accountId, hotelId } — the navbar chain (and hotel) scopes it to that client's code (HIL-154).
    listCustomCode: (filters = {}) => {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(typeof filters === 'object' && filters ? filters : { marketplaceId: filters })) {
            if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
        }
        return api.get(`/admin/legacy-uapi/custom-code${qs.toString() ? `?${qs}` : ''}`);
    },
    getCustomCode: (id) => api.get(`/admin/legacy-uapi/custom-code/${id}`),
    // `accessId` (a binding) or `routeId` (a template route) attaches the new snippet in the same call.
    // Created inactive.
    createCustomCode: (data) => api.post('/admin/legacy-uapi/custom-code', data),
    // A PATCH of only what changed — { name, description, code, active }.
    updateCustomCode: (id, data) => api.patch(`/admin/legacy-uapi/custom-code/${id}`, data),
    deleteCustomCode: (id) => api.delete(`/admin/legacy-uapi/custom-code/${id}`),
    // messages: [{ role, content }], snippetContext: { id, marketplaceId, code } -> { reply }
    assistCustomCode: (messages, snippetContext) =>
        api.post('/admin/legacy-uapi/custom-code-assistant', { messages, snippetContext }).then(r => r.data),
    // One binding: { attached (in run order), available (same system, not attached) }.
    listAccessCustomCode: (accessId) => api.get(`/admin/legacy-uapi/access/${accessId}/custom-code`),
    attachAccessCustomCode: (accessId, customCodeId) =>
        api.post(`/admin/legacy-uapi/access/${accessId}/custom-code`, { customCodeId }),
    setAccessCustomCodeSequence: (accessId, customCodeId, sequence) =>
        api.patch(`/admin/legacy-uapi/access/${accessId}/custom-code/${customCodeId}`, { sequence }),
    detachAccessCustomCode: (accessId, customCodeId) =>
        api.delete(`/admin/legacy-uapi/access/${accessId}/custom-code/${customCodeId}`),
    // The same four for a pipeline template's route (V533) — code the template runs around the route's
    // integration on every implementation. The GET also answers `target` { integration_id, marketplace_id,
    // system_name }, which is how a screen opened from the designer finds its way back.
    listRouteCustomCode: (routeId) => api.get(`/admin/legacy-uapi/pipeline-route/${routeId}/custom-code`),
    attachRouteCustomCode: (routeId, customCodeId) =>
        api.post(`/admin/legacy-uapi/pipeline-route/${routeId}/custom-code`, { customCodeId }),
    setRouteCustomCodeSequence: (routeId, customCodeId, sequence) =>
        api.patch(`/admin/legacy-uapi/pipeline-route/${routeId}/custom-code/${customCodeId}`, { sequence }),
    detachRouteCustomCode: (routeId, customCodeId) =>
        api.delete(`/admin/legacy-uapi/pipeline-route/${routeId}/custom-code/${customCodeId}`),

    // Module grants on a hotel's Universal API credential (uapi_credential_module, V420) — what the
    // caller may ASK FOR, as opposed to who it is (the credential) or where it is routed (x-hotelid).
    // The routes it may be granted come back on the same response, so the picker can never offer one
    // the server would reject.
    listCredentialModules: (credentialId) =>
        api.get(`/admin/legacy-uapi/credentials/${credentialId}/modules`),
    // WHAT THE CALLER MAY ACTUALLY REACH, as the gateway decides it (/opt/core/uapi-entitlement): the binding's
    // override, else the access profile (PROFILE mode) or the credential's own grants (DIRECT mode).
    // → { effective: { source: 'access'|'profile'|'credential', profileName?, modules: [{ id, path, name, partial }] } }
    getEffectiveModules: (credentialId, accessId) =>
        api.get(`/admin/legacy-uapi/credentials/${credentialId}/modules`,
            { params: { effective: 1, ...(accessId ? { accessId } : {}) } }),
    // Grants several at once — the picker is multi-select, and one request per module would leave a
    // half-applied list if the third of five failed.
    addCredentialModules: (credentialId, uapiRouteIds) =>
        api.post(`/admin/legacy-uapi/credentials/${credentialId}/modules`, { uapiRouteIds }),
    removeCredentialModule: (credentialId, moduleId) =>
        api.delete(`/admin/legacy-uapi/credentials/${credentialId}/modules/${moduleId}`),

    // IP allow-listing on a credential (uapi_credential_ip_policy / _ip_range, V461) — WHERE the caller
    // may connect from, enforced by the Universal API token endpoint. The toggle and the ranges arrive
    // together because they are one decision: enabled with an empty list refuses every request.
    getCredentialIpAllow: (credentialId) =>
        api.get(`/admin/legacy-uapi/credentials/${credentialId}/ip-allow`),
    setCredentialIpAllow: (credentialId, enabled) =>
        api.put(`/admin/legacy-uapi/credentials/${credentialId}/ip-allow`, { enabled }),
    // The server normalises the CIDR and returns the full list back, so the screen never has to guess
    // what was actually stored.
    addCredentialIpRange: (credentialId, cidr, description) =>
        api.post(`/admin/legacy-uapi/credentials/${credentialId}/ip-allow`, { cidr, description }),
    removeCredentialIpRange: (credentialId, rangeId) =>
        api.delete(`/admin/legacy-uapi/credentials/${credentialId}/ip-allow/${rangeId}`),


    // Payload definitions imported from Oracle's OHIP specs (V424). The list comes back WITHOUT the
    // schemas — one module resolves to megabytes — and a schema is fetched when one is opened.
    listRoutePayloads: (routeId) => api.get(`/admin/legacy-uapi/routes/${routeId}/payload`),
    getPayload: (payloadId) => api.get(`/admin/legacy-uapi/payload/${payloadId}`),
    // What the spec contains, without writing anything — so the operations can be chosen first. The
    // schemas are left out of the answer; none of them is needed to decide.
    previewRoutePayloads: (routeId, specFile) =>
        api.post(`/admin/legacy-uapi/routes/${routeId}/payload/import`,
            { preview: true, ...(specFile ? { specFile } : {}) }),
    // Imports payloads. `operationPaths` says which to store — every operation may carry its own body —
    // and `messagePath` designates the one the module's Payload tab shows. Two questions, two arguments.
    importRoutePayloads: (routeId, specFile, operationPaths, messagePath) =>
        api.post(`/admin/legacy-uapi/routes/${routeId}/payload/import`, {
            ...(specFile ? { specFile } : {}),
            ...(operationPaths && operationPaths.length ? { operationPaths } : {}),
            ...(messagePath ? { messagePath } : {}),
        }),
    setRouteMessage: (routeId, operationPath) =>
        api.put(`/admin/legacy-uapi/routes/${routeId}/message`, { operationPath }),

    // The module as a Swagger 2.0 document — the same shape Oracle publishes, built from this route's
    // operations and stored payloads.
    getRouteOpenApi: (routeId) => api.get(`/admin/legacy-uapi/routes/${routeId}/openapi`),

    // Every payload with the URL it maps to — the catalogue, without the schemas.
    listPayloads: () => api.get('/admin/legacy-uapi/payloads'),

    // ── URL-first mapping ───────────────────────────────────────────────────
    // The screen lists OUR URLs and asks, per URL, whether it has a body. `automap` works the answer out
    // from the spec the URL's module names — same path, and a method the spec declares for it — and
    // attaches it. It answers 200 with { ok: false, reason } when it will not guess, because "the spec
    // publishes nothing here" is an answer the row has to show, not a failed request.
    listOperationMapping: () => api.get('/admin/legacy-uapi/operations/mapping'),
    automapOperation: (operationId) => api.post(`/admin/legacy-uapi/operations/${operationId}/automap`),
    automapAll: () => api.post('/admin/legacy-uapi/operations/automap-all'),

    // ── spec -> body -> URLs -> name (V429) ─────────────────────────────────
    // The order the work is actually done in. `listSpecs` is Oracle's published files annotated with what
    // we hold from each; `readSpec` reads one and returns the bodies it publishes together with every URL
    // of ours, so the mapping can be stated without a second round trip.
    listSpecs: () => api.get('/admin/legacy-uapi/specs'),
    readSpec: (specFile) => api.post('/admin/legacy-uapi/spec/payloads', { specFile }),
    // `operationIds` may legitimately be empty — a payload imported and not yet mapped is a real state.
    //
    // `mirrorMethods` names methods of this payload's own path to CREATE in our Universal API because we
    // do not expose them yet — the pass-through case. The server derives the path from the spec (it holds
    // the basePath/relative split) rather than trusting one built here, and joins the new operation ids
    // to `operationIds`, so one save can serve a mix of existing and newly mirrored URLs.
    // `bodySource` says WHICH document at that path: 'response' (the GET's) or 'request' (a write's own
    // body). A path can publish both, and sending the wrong one is what validated creates against a read
    // schema (HIL-97).
    createPayload: ({ specFile, operationPath, bodySource, name, operationIds, mirrorMethods }) =>
        api.post('/admin/legacy-uapi/payloads', {
            specFile, operationPath, name, operationIds,
            ...(bodySource ? { bodySource } : {}),
            ...(mirrorMethods && mirrorMethods.length ? { mirrorMethods } : {}),
        }),
    // Rename, re-map, or both. Fields left out are left alone; an empty `operationIds` array clears the
    // mapping, which is why it is only sent when the caller means it.
    updatePayload: (payloadId, patch) => api.put(`/admin/legacy-uapi/payload/${payloadId}`, patch),
    deletePayload: (payloadId) => api.delete(`/admin/legacy-uapi/payload/${payloadId}`),

    // How much of a module a grant covers (V438). A grant of /rsv used to carry all 58 of its
    // operations, so a caller allowed to read a reservation was allowed to cancel one.
    //   mode 'ALL'       every operation under it, including ones imported later
    //   mode 'SELECTED'  only the operations listed — anything else under the module is refused
    // `level` is 'credential' (what the property may reach at all) or 'access' (through one backend).
    //
    // A SELECTED grant may also name the REDACTION each call is served with (V458):
    // `operations: [{ id, redactionSetId }]`, where a null redactionSetId means resolve by audience —
    // partner, chain, hotel, x-hotelid, then the payload's default — exactly as an ALL grant does. That
    // is why only a narrowed grant can carry them: 'ALL' covers operations that do not exist yet, and
    // one of those cannot have been given a redaction.
    //
    // `listModuleOperations` returns { operations, redactionSets }. The sets are keyed by payload_id,
    // not by operation, because one document is shared by every URL that carries it — join them on the
    // operation's own payload_id, and offer nothing where it is null (an unmapped call has no document,
    // so there is nothing to redact).
    listModuleOperations: (routeId) =>
        api.get(`/admin/legacy-uapi/routes/${routeId}/module-operations`),
    setModuleGrant: (level, moduleId, { mode, operations }) =>
        api.put(`/admin/legacy-uapi/module-grant/${level}/${moduleId}`, { mode, operations }),

    // Redactions (V431/V432/V433) - what to strip from a payload before it leaves the Universal API.
    //
    // A redaction is a NAMED SET of field rules, and a payload has several: two consumers of the same
    // call can be given different views of it. Every payload has a default set that redacts nothing -
    // the baseline a consumer gets when nobody has decided otherwise. It cannot be renamed, deleted, or
    // given rules, so there is no call here to do any of those.
    //
    // A redaction is also ADDRESSED (V436): to a partner, a chain, a hotel, or one x-hotelid. That is how
    // the Universal API picks which one a caller gets — most specific wins, and the default is the last
    // resort. `target` is { level, id } or null for "addressed to nobody"; omit it to leave it alone.
    listRedactions: (payloadId) => api.get(`/admin/legacy-uapi/payload/${payloadId}/redactions`),
    // The partners, chains, hotels and x-hotelids a redaction can be addressed to. One call: the picker
    // offers all four and would otherwise render half-populated.
    // Raise a Jira from the test console: the request, the response, the binding it routed through and
    // the transaction id. The server holds the Jira credentials and builds the description; the token
    // is deliberately not part of the payload.
    createCallJira: (payload) => api.post('/admin/legacy-uapi/jira', payload),

    // Every step of one Universal API request, by the transaction id the gateway minted for it. Kept
    // for 3 days — an older id legitimately returns nothing, which the response says in words.
    // Which redaction each operation will actually use, for this credential at this property. Resolved
    // server-side with the gateway's own functions, so the surface screen cannot promise something the
    // request then does differently.
    getOperationRedactions: ({ credentialId, hotelId, operationIds }) => {
        const qs = new URLSearchParams({ operationIds: (operationIds || []).join(',') });
        if (credentialId) qs.set('credentialId', credentialId);
        if (hotelId) qs.set('hotelId', hotelId);
        return api.get(`/admin/legacy-uapi/operations/redaction?${qs}`);
    },

    // Access profiles — a named, reusable set of routes and the redaction each is served with.
    // `modules` travels with create and update so a profile is saved in one action rather than created
    // empty and then filled, which leaves a half-made row behind any failure in the second call.
    // The address this browser reaches the platform from, resolved by the same helper the Universal API
    // checks an allow-list with — so what the button offers is what the gateway will compare.
    // Every redaction set on the estate, for the Redactions tab. Grouped by the document it redacts,
    // with what uses it — a set named on an access profile reaches everything that profile governs.
    listAllRedactionSets: () => api.get('/admin/legacy-uapi/redaction-sets'),

    // Copy a redaction set. The OWNER comes from where you are: no chain or hotel means another system
    // set (Admin), naming one means the copy belongs to that chain or property.
    duplicateRedactionSet: (setId, { name, accountId = null, hotelId = null }) =>
        api.post(`/admin/legacy-uapi/redaction-set/${setId}/duplicate`, { name, accountId, hotelId }),

    clientIp: () => api.get('/admin/legacy-uapi/client-ip'),

    listAccessProfiles: () => api.get('/admin/legacy-uapi/access-profiles'),

    // Which profile one credential follows, and what it could follow instead. The override is written
    // HERE rather than into the profile's target list, because it is an exception about a credential and
    // belongs on the screen that shows what that credential can already do.
    getCredentialAccessProfile: (credentialId) =>
        api.get(`/admin/legacy-uapi/credentials/${credentialId}/access-profile`),
    // profileId null puts the credential back on whatever it inherits from its hotel or chain.
    setCredentialAccessProfile: (credentialId, profileId) =>
        api.put(`/admin/legacy-uapi/credentials/${credentialId}/access-profile`, { profileId: profileId || null }),
    // WHICH SOURCE GOVERNS THE CREDENTIAL (V522): 'PROFILE' or 'DIRECT'. One or the other — the other
    // side's rows are kept, just not read, so switching back is not a re-entry job.
    setCredentialGrantMode: (credentialId, mode) =>
        api.put(`/admin/legacy-uapi/credentials/${credentialId}/grant-mode`, { mode }),
    getAccessProfile: (id) => api.get(`/admin/legacy-uapi/access-profiles/${id}`),
    createAccessProfile: (payload) => api.post('/admin/legacy-uapi/access-profiles', payload),
    updateAccessProfile: (id, payload) => api.patch(`/admin/legacy-uapi/access-profiles/${id}`, payload),
    deleteAccessProfile: (id) => api.delete(`/admin/legacy-uapi/access-profiles/${id}`),

    // What a profile can be pointed at, in one chain: the chain itself, its hotels, and the inbound
    // Universal API credentials those hotels hold. Each row says which profile governs it today, so the
    // form can show a target as taken rather than offer it and be refused on save.
    listAccessProfileTargetOptions: (accountId) =>
        api.get(`/admin/legacy-uapi/access-profile-targets?accountId=${accountId}`),

    getTrace: (transactionId) => api.get(`/admin/legacy-uapi/trace/${encodeURIComponent(transactionId)}`),
    listRedactionTargets: () => api.get('/admin/legacy-uapi/redaction-targets'),
    // Where a SHAPE rule actually lands. The same type name is a DIFFERENT type in each of Oracle's
    // specs, so a rule can name a field the reservation's copy of the shape does not have — and match
    // nothing there, silently. Asked before Save, never after.
    redactionCoverage: (type, path) =>
        api.get('/admin/legacy-uapi/redaction-coverage', { params: { type, path: path || '' } }),
    // The OWNER comes from the caller: nothing means a system set, which is what Admin creates; the
    // Universal API screens pass the chain or hotel they are working in.
    createRedactionSet: (payloadId, { name, description, accountId = null, hotelId = null }) =>
        api.post(`/admin/legacy-uapi/payload/${payloadId}/redactions`,
            { name, description, accountId, hotelId }),
    // A redaction covers as many audiences as it needs, each one a navbar selection narrowing left to
    // right. Only partner_id is required — {partner, chain} means every hotel in that chain.
    addRedactionScope: (setId, sel) =>
        api.post(`/admin/legacy-uapi/redaction-set/${setId}/scopes`, sel),
    deleteRedactionScope: (scopeId) => api.delete(`/admin/legacy-uapi/redaction-scope/${scopeId}`),
    // The WHOLE rule list of one set, every time. The screen removes rules by deleting cards, so an
    // omitted path means "stop redacting it" - a merge could never express that. [] clears them.
    setRedactions: (setId, redactions) =>
        api.put(`/admin/legacy-uapi/redaction-set/${setId}`, { redactions }),
    renameRedactionSet: (setId, { name, description }) =>
        api.put(`/admin/legacy-uapi/redaction-set/${setId}`, { name, description }),
    deleteRedactionSet: (setId) => api.delete(`/admin/legacy-uapi/redaction-set/${setId}`),

    // Map ONE of a spec's payloads to ONE of our operations. `preview` lists what the spec publishes;
    // the second call commits the mapping. Explicit, because /rsv alone publishes 58 operations and
    // which payload describes which of our calls is a decision rather than a string match.
    previewOperationPayloads: (operationId, specFile) =>
        api.post(`/admin/legacy-uapi/operations/${operationId}/payload/import`,
            { preview: true, ...(specFile ? { specFile } : {}) }),
    // `operationIds` is the set of OUR URLs this body serves — normally several, since OHIP's GET, POST
    // and PUT of one resource carry the same document.
    mapPayloadToOperation: (operationId, specFile, operationPath, operationIds) =>
        api.post(`/admin/legacy-uapi/operations/${operationId}/payload/import`,
            { ...(specFile ? { specFile } : {}), operationPath,
              ...(operationIds && operationIds.length ? { operationIds } : {}) }),

    // Re-map an existing payload without re-importing it — changing which methods carry a body should
    // not mean fetching a 2 MB spec again.
    setPayloadOperations: (payloadId, operationIds) =>
        api.put(`/admin/legacy-uapi/payload/${payloadId}/operations`, { operationIds }),

    // One operation's body, as JSON Schema.
    getOperationPayload: (operationId) =>
        api.get(`/admin/legacy-uapi/operations/${operationId}/payload`),

    // The operations a module's spec publishes, and creating the chosen ones on the route. `preview`
    // first — nothing is written until the picker says which.
    previewSpecOperations: (routeId, specFile) =>
        api.post(`/admin/legacy-uapi/routes/${routeId}/operations/import`,
            { preview: true, ...(specFile ? { specFile } : {}) }),
    importSpecOperations: (routeId, specFile, operations) =>
        api.post(`/admin/legacy-uapi/routes/${routeId}/operations/import`, {
            ...(specFile ? { specFile } : {}), operations,
        }),

    listAllOperations: () => api.get('/admin/legacy-uapi/operations'),
    listOperations: (routeId) => api.get(`/admin/legacy-uapi/routes/${routeId}/operations`),
    // Every operation the Universal API publishes, across all routes, each carrying its route path as
    // `full_path` — the flat list a picker needs, where listOperations answers for one module at a time.
    listAllOperations: () => api.get('/admin/legacy-uapi/operations'),
    createOperation: (routeId, data) => api.post(`/admin/legacy-uapi/routes/${routeId}/operations`, data),
    updateOperation: (id, data) => api.patch(`/admin/legacy-uapi/operations/${id}`, data),
    deleteOperation: (id) => api.delete(`/admin/legacy-uapi/operations/${id}`),
};

export default legacyUapiService;
